import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@lumika/ui";
import {
  getHotkeys,
  subscribeHotkeys,
  useHotkeys,
  useHotkeysRoot,
  type Hotkey,
} from "../lib/hotkeys";

/**
 * `?` keyboard cheat-sheet (UIUX §5.1). Rendered straight from the live
 * hotkey registry — a binding that isn't registered can't appear here,
 * and one that is can't be omitted, so the overlay never drifts from
 * reality. Also hosts the single document-level dispatcher (AppShell
 * mounts this once).
 */
export function HotkeysHelp() {
  useHotkeysRoot();
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState(0);

  useHotkeys([
    {
      keys: "?",
      scope: "Global",
      description: "Show keyboard shortcuts",
      onTrigger: () => setOpen(true),
    },
  ]);

  // Re-read the registry when bindings mount/unmount while open.
  useEffect(() => subscribeHotkeys(() => setVersion((v) => v + 1)), []);

  const groups = useMemo(() => {
    void version;
    if (!open) return [];
    const byScope = new Map<string, Hotkey[]>();
    const seen = new Set<string>();
    for (const hk of getHotkeys()) {
      const dedupeKey = `${hk.scope}|${hk.keys}|${hk.description}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const list = byScope.get(hk.scope) ?? [];
      list.push(hk);
      byScope.set(hk.scope, list);
    }
    return [...byScope.entries()];
  }, [open, version]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Single keys work whenever you&rsquo;re not typing in a field.
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          {groups.map(([scope, hotkeys]) => (
            <section key={scope}>
              <h3 className="mb-1.5 text-sm font-semibold text-muted-foreground">
                {scope}
              </h3>
              <ul className="grid gap-1">
                {hotkeys.map((hk) => (
                  <li
                    key={`${hk.keys}-${hk.description}`}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="text-foreground">{hk.description}</span>
                    <kbd className="shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                      {hk.keys}
                    </kbd>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
